/**
 * useWorkflowModule - Interruptor do módulo Workflow no tenant
 *
 * Após a remoção do MVP (v1), este hook cuida apenas da flag `workflow_enabled`
 * do tenant: leitura para os guards/menus e alternância em Configurações ›
 * Workflow. A autoria das esteiras passa a ser entregue pelo motor v2.
 *
 * @version 2.0.0
 * RELEASE: 2026-08-30 v2.0.0 - Workflow 2.0 Fase 0: removidas as mutations do MVP v1
 *          (definições, modelos, status, backfill). Mantida apenas a flag do módulo.
 * RELEASE: 2026-08-30 v1.1.0 - Escopo multi-workspace e aplicação de modelo pronto
 * RELEASE: 2026-08-29 v1.0.0 - Criação inicial (Fase 1)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

const logger = createLogger('useWorkflowModule');

/**
 * Hook central do módulo Workflow.
 * Usado pelo guard de rota, pelo menu e pela aba de configurações.
 */
export function useWorkflowModule() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = currentTenant?.id;

  const query = useQuery({
    queryKey: ['workflow-module', tenantId],
    enabled: !!tenantId,
    staleTime: 60_000,
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await supabase
        .from('tenants')
        .select('workflow_enabled')
        .eq('id', tenantId!)
        .maybeSingle();

      if (error) {
        logger.error('Erro ao carregar a flag do módulo Workflow', { error: error.message });
        throw error;
      }

      return Boolean((data as { workflow_enabled?: boolean } | null)?.workflow_enabled);
    },
  });

  /** Invalida o cache da flag */
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['workflow-module', tenantId] });
  };

  /** Liga/desliga o módulo para o tenant (somente admin — validado na RPC) */
  const toggleModule = useMutation({
    mutationFn: async (enabled: boolean) => {
      const { error } = await (supabase.rpc as any)('update_workflow_module_settings', {
        p_enabled: enabled,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) throw error;
      return enabled;
    },
    onSuccess: (enabled) => {
      invalidate();
      toast.success(enabled ? 'Módulo Workflow habilitado' : 'Módulo Workflow desabilitado');
    },
    onError: (error: any) => {
      logger.error('Erro ao alterar flag do módulo Workflow', { error: error?.message });
      toast.error(error?.message || 'Não foi possível alterar o módulo Workflow');
    },
  });

  return {
    /** Flag `workflow_enabled` do tenant */
    isEnabled: query.data ?? false,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    refetch: query.refetch,
    invalidate,
    toggleModule,
  };
}
